import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import { usePrintContext } from '@contexts/PrintContext';
import { ButtonType, ConfigDto, SampleDetailsDto } from '@src/types/types';
import { SampleDone } from '@components/sample-details/SampleDone';
import { Button } from '@components/atoms/Button';
import { SampleHeader } from '@components/sample-details/SampleHeader.tsx';
import { UserInfo } from '@components/sample-details/UserInfo.tsx';
import { ShipmentStatus } from '@components/organisms/ShipmentStatus.tsx';
import { Logo } from '@components/atoms/Logo.tsx';

interface SampleDonePdfProps {
  sample: SampleDetailsDto;
  config: ConfigDto;
}

export const SampleDonePdf = ({ sample, config }: SampleDonePdfProps) => {
  const { t } = useTranslation('common');
  const contentRef = useRef<HTMLDivElement>(null);
  const { setPrinting, isPrinting } = usePrintContext();

  // Setup react-to-print with special classes for print
  const reactToPrintFn = useReactToPrint({
    contentRef,
    bodyClass:
      'w-full min-w-[1300px] flex justify-center print:min-w-[1300px] print:w-full print:flex print:justify-center',
    // Function to prepare the document before printing
    onBeforePrint: () => Promise.resolve(setPrinting(true)),
    // Cleanup after printing
    onAfterPrint: () => setPrinting(false),
  });

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Button
          buttonType={ButtonType.PRIMARY}
          label={t('downloadPdf')}
          onClick={reactToPrintFn}
        />
      </div>
      <div className="print:min-w-[1300px]" ref={contentRef}>
        <div className="hidden print:block mb-8">
          <Logo />
        </div>
        <SampleHeader
          sample={sample}
          className="hidden print:flex print:mb-8"
        />
        <div className="hidden print:grid grid-cols-2 gap-8 mb-8">
          <UserInfo sample={sample} forceExpand={isPrinting} />
          <ShipmentStatus sample={sample} />
        </div>
        <SampleDone sample={sample} config={config} />
      </div>
    </div>
  );
};
